/**
 * Input de suma, tolerant la felul in care tasteaza un contabil roman:
 *
 *   `1.234,56`  `1234.56`  `1 234,56`  `250000`  `(1.500)`  `=2515,67+2501,07`
 *
 * `.` si `,` sunt AMBELE separator zecimal (memorie musculara de tastatura numerica), spatiile
 * si separatorii de mii sunt ignorati, iar expresiile in stil Excel se salveaza atat ca valoare
 * cat si ca expresie — 69 de celule din foaia principala sunt chiar expresii.
 *
 * Parsarea nu e implementata aici: vine din `@evomag/core/money`, singurul loc unde se
 * rotunjeste o suma in toata aplicatia.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { parseAmountInput } from '@evomag/core/money';
import { formatMinor } from '@evomag/core/format';

export interface PropsAmountInput {
  /** Valoarea curenta in unitati minore, sau `null` pentru celula goala. */
  value: number | null;
  /**
   * Se apeleaza la commit (blur sau `Enter`), niciodata la fiecare tasta — o mutatie per
   * caracter ar inunda coada de salvare.
   */
  onCommit: (minor: number | null, expression: string | null) => void;
  /** Nume accesibil obligatoriu: intr-o grila, `<label>` vizibil nu exista. */
  eticheta: string;
  scalaMinora?: number;
  moneda?: string | null;
  disabled?: boolean;
  className?: string;
  /** Expresia salvata anterior, ca badge-ul `=` sa supravietuiasca unui reload. */
  expresie?: string | null;
}

const AJUTOR = 'Acceptă 1.234,56 · 1234.56 · =1000+250';

export function AmountInput({
  value,
  onCommit,
  eticheta,
  scalaMinora = 100,
  moneda = 'lei',
  disabled = false,
  className,
  expresie = null,
}: PropsAmountInput) {
  const idAjutor = useId();
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(() => textDin(value, scalaMinora));
  /**
   * Ultimul text pe care l-a scris COMPONENTA in camp, nu omul.
   *
   * `normalizeaza` ruleaza si la `Enter`, si la `blur` — deci dupa `Enter` urmeaza aproape mereu un
   * al doilea apel, pe un camp pe care nimeni nu l-a mai atins. Fara marcajul asta, al doilea apel
   * reparseaza textul deja formatat (`1.540,50`), nu mai gaseste expresia in el, si comite `null`
   * peste ea: `=1200+340,50` tastat, salvat corect, apoi STERS de propriul blur.
   */
  const textNormalizat = useRef<string | null>(null);
  const [eroare, setEroare] = useState(false);
  const [expresieLocala, setExpresieLocala] = useState<string | null>(expresie);

  // Valoarea poate veni si din afara (fill down, paste TSV, salvare de la alt utilizator).
  // Nu o suprascriem cat timp campul e in editare — ar sterge ce tasteaza omul.
  useEffect(() => {
    if (document.activeElement !== ref.current) {
      const proaspat = textDin(value, scalaMinora);
      setText(proaspat);
      textNormalizat.current = proaspat;
      setEroare(false);
    }
  }, [value, scalaMinora]);

  function normalizeaza(): void {
    // Nimeni n-a tastat de la ultima normalizare: `blur`-ul de dupa `Enter` nu are ce sa comite.
    if (textNormalizat.current !== null && text === textNormalizat.current) return;

    const brut = text.trim();
    if (brut === '') {
      setEroare(false);
      setExpresieLocala(null);
      textNormalizat.current = text;
      if (value !== null) onCommit(null, null);
      return;
    }

    const parsat = parseAmountInput(brut, scalaMinora);
    if (parsat === null) {
      // Valoarea tastata RAMANE in camp — nu stergem munca nimanui pentru o virgula.
      setEroare(true);
      return;
    }

    setEroare(false);
    setExpresieLocala(parsat.expression);
    const formatat = formatMinor(parsat.minor, { minorScale: scalaMinora });
    setText(formatat);
    textNormalizat.current = formatat;
    if (parsat.minor !== value || parsat.expression !== expresieLocala) {
      onCommit(parsat.minor, parsat.expression);
    }
  }

  return (
    <span className={`inline-flex items-center gap-1 ${className ?? ''}`}>
      <input
        ref={ref}
        className="input num text-right"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        value={text}
        aria-label={eticheta}
        aria-invalid={eroare}
        aria-describedby={idAjutor}
        onChange={(e) => {
          setText(e.target.value);
          if (eroare) setEroare(false);
        }}
        onBlur={normalizeaza}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            normalizeaza();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            const initial = textDin(value, scalaMinora);
            setText(initial);
            textNormalizat.current = initial;
            setEroare(false);
          }
        }}
      />

      {moneda ? <span className="text-11 text-ink-3">{moneda}</span> : null}

      {expresieLocala ? (
        <span className="badge num" title={`Derivat din ${expresieLocala}`}>
          =
        </span>
      ) : null}

      <span id={idAjutor} className={eroare ? 'text-11 text-neg' : 'doar-sr'} role="status">
        {eroare ? `Nu pot citi suma. ${AJUTOR}` : AJUTOR}
      </span>
    </span>
  );
}

function textDin(minor: number | null, scalaMinora: number): string {
  return minor === null ? '' : formatMinor(minor, { minorScale: scalaMinora });
}
